import { useEffect, useState } from 'react';
import CountrySelect from './CountrySelect';
import { Phone } from 'lucide-react';
import { logger } from '../services/logger';

const CallRateCalculator = () => {
  const [selectedCountry, setSelectedCountry] = useState({
    name: "United States",
    dialCode: "+1",
    isoCode: "US",
    emoji: "🇺🇸"
  });

  const [minutes, setMinutes] = useState(5);
  const [rate, setRate] = useState(0.02); // Default fallback

  // Fetch live rate from backend on country change
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_BASE_URL}/api/rates/${selectedCountry.isoCode}`);
        const data = await res.json();
        if (res.ok && data.ourRate) {
          setRate(data.ourRate);
        } else {
          setRate(0.02); // Fallback if not found
        }
      } catch (err) {
        logger.error(err, 'Failed to fetch rate');
        setRate(0.02); // Fallback on error
      }
    };
    if (selectedCountry) {
        fetchRate();
    }
  }, [selectedCountry]);

  const total = (Number(minutes) * rate).toFixed(2);

  return (
    <div className="flex flex-col justify-between h-full bg-white rounded-2xl p-6 shadow-xl border border-purple-100">
      <div className="space-y-6">
        <h3 className="text-gray-900 text-xl font-semibold flex items-center gap-2">
          <Phone className="text-purple-500" /> Rate Calculator
        </h3>

        <p className='text-gray-700 mb-3'>
          Calculate how many credits you need.
        </p>

        <div>
          <p className="text-gray-700 mb-1">I'm calling</p>
          <div className="w-full">
            <CountrySelect
              selected={selectedCountry}
              onSelect={setSelectedCountry}
              fullWidth
            />
          </div>
        </div>

        <div>
          <label className="text-gray-700 mb-1 block">For</label>
          <input
            type="number"
            min={1}
            value={minutes}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setMinutes(value);
              } else {
                const num = Number(value);
                if (num >= 1) {
                  setMinutes(num);
                }
              }
            }}
            className="w-full p-2 bg-white border-2 border-purple-300 text-gray-900 text-lg rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <span className="text-gray-600 text-sm mt-1 block">minutes</span>
        </div>

        <div className="bg-purple-50 rounded-lg p-4 text-center space-y-2 border border-purple-100">
          <p className="text-gray-800">Your call will cost</p>
          <p className="text-2xl font-bold text-purple-700">${total}</p>
          <div className="border-t border-purple-200 my-2 w-2/3 mx-auto" />
          <p className="text-sm text-gray-700">
            <span className="text-purple-600 font-semibold">${rate.toFixed(2)}</span> per minute
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-2">
        <p className="text-sm text-gray-600 flex items-start gap-2">
          *Partial minutes are rounded up when billing and these are approximate averages for each country. The exact rate is visible when you dial a number.
        </p>
      </div>
    </div>
  );
};

export default CallRateCalculator;
